import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  RangeSelect,
  IncludeTestToggle,
  useIncludeTest,
  centsToUsd,
  TestDataBanner,
} from "./adminUi";

export default function AdminRevenuePage() {
  const [range, setRange] = useState("30d");
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    adminApi.revenue({ range, include_test: includeTest }).then(setData).catch(() => {});
  }, [range, includeTest]);

  return (
    <div data-testid="admin-revenue-page" className="space-y-8">
      <PageHeader
        eyebrow="Business"
        title="Revenue"
        subtitle="MRR, new subscriptions, cancellations, gift revenue, sponsor-attributed revenue, and trend."
        right={
          <div className="flex items-center gap-3">
            <IncludeTestToggle value={includeTest} onChange={setIncludeTest} />
            <RangeSelect value={range} onChange={setRange} testid="rev-range" />
          </div>
        }
      />

      <TestDataBanner show={includeTest} />

      <section>
        <SectionHeader label="Headline" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi
            testid="rev-mrr"
            label="Current MRR"
            value={centsToUsd(data?.mrr_cents)}
            tone="accent"
            hint={data?.mrr_notes ? "Per-subscription price (local)" : undefined}
          />
          <Kpi testid="rev-paid-pro" label="Paid Pro subscribers" value={data?.paid_pro} />
          <Kpi testid="rev-new-paid" label="New paid (range)" value={data?.new_paid_in_range} />
          <Kpi testid="rev-canc" label="Cancellations (range)" value={data?.cancellations_in_range} />
          <Kpi testid="rev-sub" label="Subscription revenue" value={centsToUsd(data?.subscription_revenue_cents)} />
          <Kpi testid="rev-gift" label="Gift revenue" value={centsToUsd(data?.gift_revenue_cents)} />
          <Kpi testid="rev-sp-paid" label="Sponsor-attributed paid" value={data?.sponsor_attributed?.paid} />
          <Kpi testid="rev-sp-gifts" label="Sponsor-attributed gifts" value={data?.sponsor_attributed?.gift_events} />
        </div>
      </section>

      <section>
        <SectionHeader label="Revenue trend" />
        {data && data.trend?.length ? (
          <Card>
            <RevenueTrend data={data.trend} />
          </Card>
        ) : (
          <div className="text-sm text-[#8FA894]">No revenue in this window.</div>
        )}
      </section>

      <section>
        <Card>
          <div className="grid sm:grid-cols-3 gap-4 text-[11px] text-[#8FA894] leading-relaxed">
            <div>
              <div className="uppercase tracking-[0.16em] text-[#B7D2AF] mb-1">MRR</div>
              {data?.definitions?.mrr ||
                "Recurring run-rate: sum of active subscribers' locally cached recurring prices, yearly plans normalized to monthly."}
            </div>
            <div>
              <div className="uppercase tracking-[0.16em] text-[#B7D2AF] mb-1">Subscription revenue</div>
              {data?.definitions?.subscription_revenue ||
                "Cash actually collected in the selected date range from granted subscription checkouts."}
            </div>
            <div>
              <div className="uppercase tracking-[0.16em] text-[#B7D2AF] mb-1">Gift revenue</div>
              {data?.definitions?.gift_revenue ||
                "Sum of gift order amount_cents for gifts paid/scheduled/redeemed within the selected date range."}
            </div>
          </div>
          <div className="mt-3 text-[11px] text-[#7C8F7C]">
            All figures respect the persisted <code>is_test</code> flag. Toggle "Include test/demo data" to fold seeded records back in.
          </div>
        </Card>
      </section>
    </div>
  );
}

function RevenueTrend({ data }) {
  const max = Math.max(
    1,
    ...data.map((d) => (d.subscription_cents || 0) + (d.gift_cents || 0))
  );
  return (
    <div className="flex items-end gap-1.5 h-36" data-testid="rev-trend-series">
      {data.map((d) => {
        const total = (d.subscription_cents || 0) + (d.gift_cents || 0);
        const subH = ((d.subscription_cents || 0) / max) * 100;
        const giftH = ((d.gift_cents || 0) / max) * 100;
        return (
          <div key={d.bucket} className="flex-1 flex flex-col items-center justify-end gap-0.5">
            <div className="w-full flex flex-col justify-end h-full">
              <div
                title={`Gifts ${centsToUsd(d.gift_cents)} on ${d.bucket}`}
                className="w-full bg-[#B4795C]/70 rounded-t-md"
                style={{ height: `${Math.max(0, giftH)}%` }}
              />
              <div
                title={`Subs ${centsToUsd(d.subscription_cents)} on ${d.bucket}`}
                className="w-full bg-[#4F6F52]/70"
                style={{ height: `${Math.max(0, subH)}%` }}
              />
            </div>
            <div className="text-[9px] text-[#607467]">{d.bucket.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}
